import { useCallback, useRef, useState } from 'react';
import { oneAtATime } from '@versesverse/core';

/**
 * ONE ACT PER THING AT A TIME, for a panel of rows — the React wiring over `core`'s gate.
 *
 * WHAT IT IS FOR. A control that acts on one row by id, where the row stays on screen for the whole
 * round trip. Press it twice and the server answers the second one a refusal *because the first
 * worked* — `no such channel of yours`, `no such post to remove`, `no such scheduled session of
 * yours`, `nobody is waiting under that name` — and the panel says that refusal out loud, about an
 * act that succeeded. Seven such controls were found across five panels on 2026-08-21 and not one
 * of them was guarded.
 *
 * WHY THE GATE IS A REF. Two presses inside one frame both run against the render that was on
 * screen when the first landed: both read `working` as false, both call the server, and the
 * `disabled` attribute only arrives with the next render. The decision has to be made in the
 * caller's own tick, which is what `core`'s `oneAtATime` does and what a piece of state cannot.
 *
 * WHEN `disabled` ALONE IS ENOUGH, and this is not needed: when a duplicate act is harmless. The
 * account bar's sign-out is the example — its catch reports the reader signed out either way, so a
 * second call changes nothing anybody sees. **The test is not "can it be pressed twice", it is
 * "does the second press produce a sentence".**
 *
 * The rule and its tests live in `core/one-at-a-time.ts`; only the wiring below is per-renderer,
 * because `core` is React-free on purpose and the phone has its own copy of exactly this.
 */
export interface OneAtATimeHook {
  /**
   * Run `act` under `key`, or do NOTHING if that key is already running.
   *
   * Nothing, not a failure: the act being asked for is already under way, and reporting anything is
   * how the defect looked in the first place.
   */
  run(key: string, act: () => Promise<void>): Promise<void>;
  /** Whether that key is in flight, for the control to shut itself. */
  working(key: string): boolean;
}

export function useOneAtATime(): OneAtATimeHook {
  const gate = useRef(oneAtATime()).current;
  /* The mirror the controls are drawn from. `gate` is what decides; this only reports. */
  const [running, setRunning] = useState<readonly string[]>([]);

  const run = useCallback(async (key: string, act: () => Promise<void>): Promise<void> => {
    if (!gate.claim(key)) return;
    setRunning(gate.running());
    try {
      await act();
    } finally {
      gate.release(key);
      setRunning(gate.running());
    }
  }, [gate]);

  return { run, working: (key: string): boolean => running.includes(key) };
}
